import * as React from 'react';

export interface ForceUpdateTerminalProps {
  refreshForceUpdateHash?: () => void;
}

interface ForceUpdateTerminalState {
  forceUpdateHash: string;
}

export const ForceUpdateTerminalContext = React.createContext('');

/**
 * HOC withForceUpdateTerminal let us trigger udpate of sibblings components which are wrapped
 * by HOC withForceUpdateConsumer. It is done by providing new value of props by react context.
 * We should use it ONLY for cases when we aren't able to trigger update of component from inside.
 *
 * Use case:
 * - 3th part library react-textarea-autosize(RTA) and css animation somewhere in parent component.
 *  To correctly calculate height of RTA all related animations have to be finished.
 *  In a place where animation was added, I attached to 'animationend' event and then calculation
 *  (by HOC withForceUpdateTerminal) of new textarea height is triggered.
 *  Problem is also described here:
 *  https://github.com/andreypopp/react-textarea-autosize/issues/48
 */

export const withForceUpdateTerminal = <T extends object>(Component: React.ComponentType<T>) => {
  const WrappedComponentProps:React.ComponentType<T & ForceUpdateTerminalProps> = Component;
  return class ForceUpdateTerminalComponent extends React.Component<T, ForceUpdateTerminalState> {
    public state: Readonly<ForceUpdateTerminalState> = {
      forceUpdateHash: ''
    };

    private handleRefreshForceUpdateHash = () => {
      this.setState({
        forceUpdateHash: Date.now().toString()
      });
    };

    public render() {
      return (
        <ForceUpdateTerminalContext.Provider value={this.state.forceUpdateHash} >
          <WrappedComponentProps {...this.props} refreshForceUpdateHash={this.handleRefreshForceUpdateHash} />
        </ForceUpdateTerminalContext.Provider>
      );
    }
  };
};

export interface ForceUpdateConsumerProps {
  updatehash?: string;
}

export const withForceUpdateConsumer = <C extends React.ComponentType<any>>(Component: C) => {
  type Props = React.ComponentPropsWithoutRef<C>;
  type Ref = React.ComponentRef<C>;
  type ComponentWithRefProps = Props & ForceUpdateConsumerProps & React.RefAttributes<Ref>;

  const WrappedComponent = Component as React.ComponentType<ComponentWithRefProps>;

  const hoc = React.forwardRef<Ref, Props>((props, ref) => 
    <ForceUpdateTerminalContext.Consumer >
      {updateHash => <WrappedComponent {...({ ...props, updatehash: updateHash } as Props & ForceUpdateConsumerProps)} ref={ref} />}
    </ForceUpdateTerminalContext.Consumer>
  );
  hoc.displayName = `withForceUpdateConsumer(${Component.displayName || Component.name || 'TextareaAutosize'})`;
  return hoc;
};
